import React, { useEffect, useState } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import Dropzone, { DropzoneOptions } from "react-dropzone";
import { useDispatch } from "react-redux";

import { bytesToSize } from "helpers";
import { uiActions } from "store";

import UploadIcon from "static/images/upload.png";
import styles from "./dropzoneField.module.scss";

const previewCache: string[] = [];

const ChooseFileMessage = () => (
	<div className={clsx(styles.chooseFileMessage, "mt-3")}>
		Click to Choose File
		<br />
		or Drag and Drop to upload
	</div>
);

type DropzoneFieldProps = {
	input: any;
	meta: any;
	onChangeCb: (files: any | any[]) => void;
	className?: string;
	warningValidationMessage?: string;
	dropzoneClassName?: string;
	maxSize?: number;
	multiple?: boolean;
	isWarningValidationMessage?: boolean;
	maxFilesNumber?: number;
	MessageTitle?: () => JSX.Element | null;
	FileRejectedMessage?: () => JSX.Element | null;
	FileList?: ({ fileOrFiles }) => JSX.Element | null;
	accept?: string; // ".xlsx, .xls, .csv, .pdf, .txt, .zip"
};

const DropzoneField = ({
	input,
	meta: { touched, error },
	onChangeCb,
	className,
	dropzoneClassName,
	isWarningValidationMessage = false,
	warningValidationMessage,
	maxSize = 10485760, // 10 megabytes
	multiple = false,
	maxFilesNumber,
	MessageTitle = () => null,
	FileRejectedMessage = () => null,
	FileList = undefined,
	accept,
	...props
}: DropzoneFieldProps) => {
	const dispatch = useDispatch();

	const [uploadErrorMessage, setUploadErrorMessage] = useState("");

	const handleDrop: DropzoneOptions["onDrop"] = acceptedFiles => {
		if (acceptedFiles.length <= 0)
			if (isWarningValidationMessage) {
				dispatch(
					uiActions.showWarningNotification({
						title: warningValidationMessage,
					}),
				);
			} else {
				setUploadErrorMessage(
					`Size of document is more than ${bytesToSize(maxSize)}`,
				);
			}
		else setUploadErrorMessage("");

		if (multiple) {
			acceptedFiles.forEach((file: any) => {
				file.preview = URL.createObjectURL(file);
			});
			input.onChange(acceptedFiles);
			onChangeCb?.(acceptedFiles);
		} else {
			let preview = URL.createObjectURL(acceptedFiles[0]);
			(acceptedFiles[0] as any).preview = preview;

			previewCache.push(preview);

			input.onChange(acceptedFiles[0]);
			onChangeCb?.(acceptedFiles[0]);
		}

		const file = multiple ? acceptedFiles : acceptedFiles[0];

		input.onChange(file);
		onChangeCb?.(file);
	};

	useEffect(() => {
		return () => {
			previewCache.forEach(URL.revokeObjectURL);
		};
	}, []);

	return (
		<div className={className}>
			<Dropzone
				onDrop={handleDrop}
				multiple={multiple}
				maxSize={maxSize}
				accept={accept}
				{...props}
			>
				{({ getRootProps, getInputProps, rejectedFiles }: any) => {
					const _dropzoneClassName = clsx(
						styles.dropzone,
						dropzoneClassName,
					);

					return (
						<div
							{...getRootProps({ className: _dropzoneClassName })}
						>
							<input {...getInputProps()} name={input.name} />
							<img src={UploadIcon} alt="" width="100" />
							<ChooseFileMessage />
							{fp.isEmpty(input.value) && <MessageTitle />}
							{!fp.isEmpty(rejectedFiles) && (
								<FileRejectedMessage />
							)}
						</div>
					);
				}}
			</Dropzone>
			<p className={styles.errorTitle}>{uploadErrorMessage || ""}</p>
			{touched && error && <p className={styles.errorTitle}>{error}</p>}
			{input.value && FileList && <FileList fileOrFiles={input.value} />}
		</div>
	);
};

export default DropzoneField;
